/**
 * Step 2: API credentials (Zerodha/Upstox) or "No API keys required" + Next.
 */

import { Box, TextField, Typography, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import type { BrokerMetadata } from './types';

export interface WizardStep2Data {
  apiKey: string;
  apiSecret: string;
  showApiKey: boolean;
  showApiSecret: boolean;
  password?: string;
  totpSecret?: string;
}

interface BrokerRegistrationWizardStep2Props {
  broker: BrokerMetadata;
  data: WizardStep2Data;
  onChange: (data: WizardStep2Data) => void;
  requiresApiCredentials: boolean;
  authMode: 'oauth' | 'totp';
  apiKeyLabel?: string;
  apiSecretLabel?: string;
  apiKeyHelperText?: string;
  apiSecretHelperText?: string;
  step2NotRequiredCopy: string;
  error?: string;
  disabled?: boolean;
}

export function BrokerRegistrationWizardStep2({
  broker,
  data,
  onChange,
  requiresApiCredentials,
  authMode,
  apiKeyLabel = 'API Key',
  apiSecretLabel = 'API Secret',
  apiKeyHelperText,
  apiSecretHelperText,
  step2NotRequiredCopy,
  error,
  disabled = false,
}: BrokerRegistrationWizardStep2Props) {
  const shortName = broker.display_name
    ? broker.display_name.split(' ')[0]
    : broker.broker_type.charAt(0).toUpperCase() + broker.broker_type.slice(1);

  if (!requiresApiCredentials && authMode !== 'totp') {
    return (
      <Box sx={{ py: 1.5 }}>
        <Typography variant="body1" color="text.secondary">
          {step2NotRequiredCopy.replace('No API keys required for', 'API Credential not required for')} Click continue for final step.
        </Typography>
      </Box>
    );
  }

  const totpInputs = (
    <>
      <TextField
        label={
          broker.broker_type.toLowerCase() === 'dhan'
            ? 'Dhan Pin'
            : broker.broker_type.toLowerCase() === 'groww'
              ? 'Groww TOTP Token'
              : `${shortName} Password`
        }
        type="password"
        value={data.password || ''}
        onChange={(e) => onChange({ ...data, password: e.target.value })}
        placeholder={
          broker.broker_type.toLowerCase() === 'dhan'
            ? '6-digit PIN'
            : broker.broker_type.toLowerCase() === 'groww'
              ? 'Enter your Groww TOTP token'
              : `Enter your ${shortName} password`
        }
        fullWidth
        required
        size="small"
        disabled={disabled}
        error={!!error && (error.includes('Pin') || error.includes('Password') || error.includes('Token'))}
        helperText={error && (error.includes('Pin') || error.includes('Password') || error.includes('Token')) ? error : undefined}
        sx={{
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
          '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.8125rem' },
          '& .MuiInputBase-input::placeholder': { fontSize: '0.8125rem', opacity: 0.7 },
        }}
      />
      <TextField
        label={`${shortName} TOTP Secret`}
        type="password"
        value={data.totpSecret || ''}
        onChange={(e) => onChange({ ...data, totpSecret: e.target.value })}
        placeholder={`Enter your ${shortName} base32 secret`}
        fullWidth
        required
        size="small"
        disabled={disabled}
        error={!!error && error.includes('TOTP Secret')}
        helperText={error && error.includes('TOTP Secret') ? error : `Base32 secret from your ${shortName} settings`}
        sx={{
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
          '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.8125rem' },
          '& .MuiInputBase-input::placeholder': { fontSize: '0.8125rem', opacity: 0.7 },
        }}
        inputProps={{
          style: { fontFamily: 'monospace' }
        }}
      />
    </>
  );

  const apiInputs = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label={apiKeyLabel}
        type={data.showApiKey ? 'text' : 'password'}
        value={data.apiKey}
        onChange={(e) => onChange({ ...data, apiKey: e.target.value })}
        placeholder={
          broker.broker_type.toLowerCase() === 'upstox'
            ? 'From Upstox developer console / app settings'
            : 'From kite.zerodha.com → Your Apps'
        }
        fullWidth
        required
        size="small"
        disabled={disabled}
        error={!!error && (error.includes('API Key') || error.includes('API key'))}
        helperText={error && (error.includes('API Key') || error.includes('API key')) ? error : apiKeyHelperText}
        sx={{
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
          '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.8125rem' },
          '& .MuiInputBase-input::placeholder': { fontSize: '0.8125rem', opacity: 0.7 },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => onChange({ ...data, showApiKey: !data.showApiKey })}
                edge="end"
                size="small"
                aria-label={data.showApiKey ? 'Hide API key' : 'Show API key'}
              >
                {data.showApiKey ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        label={apiSecretLabel}
        type={data.showApiSecret ? 'text' : 'password'}
        value={data.apiSecret}
        onChange={(e) => onChange({ ...data, apiSecret: e.target.value })}
        placeholder={
          broker.broker_type.toLowerCase() === 'upstox'
            ? 'From Upstox developer console / app settings'
            : 'From kite.zerodha.com → Your Apps'
        }
        fullWidth
        required
        size="small"
        disabled={disabled}
        error={!!error && (error.includes('API Secret') || error.includes('API secret'))}
        helperText={error && (error.includes('API Secret') || error.includes('API secret')) ? error : apiSecretHelperText}
        sx={{
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
          '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.8125rem' },
          '& .MuiInputBase-input::placeholder': { fontSize: '0.8125rem', opacity: 0.7 },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => onChange({ ...data, showApiSecret: !data.showApiSecret })}
                edge="end"
                size="small"
                aria-label={data.showApiSecret ? 'Hide API secret' : 'Show API secret'}
              >
                {data.showApiSecret ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {requiresApiCredentials && apiInputs}
      {authMode === 'totp' && totpInputs}
    </Box>
  );
}
